import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type {
  CortesiaInput,
  FazendaDTO,
  FichaFazendaDTO,
  PatchFazenda,
  VisaoGeralDTO,
} from "@shared/plataforma-fazendas";
import type { LinkAcesso } from "@/components/admin/link-acesso-dialog";

/**
 * Plataforma › Fazendas (P1). Toda `queryFn` usa `fetchJson` (checa `r.ok`):
 * um 403 de quem não é da plataforma vira erro, não "dado".
 */
const ORGS = "/api/plataforma/orgs";

export function useVisaoGeral(ativo = true) {
  return useQuery<VisaoGeralDTO>({
    queryKey: ["/api/plataforma/visao-geral"],
    queryFn: () => fetchJson<VisaoGeralDTO>("/api/plataforma/visao-geral"),
    enabled: ativo,
  });
}

export function useFazendas(ativo = true) {
  return useQuery<FazendaDTO[]>({
    queryKey: [ORGS],
    queryFn: () => fetchJson<FazendaDTO[]>(ORGS),
    enabled: ativo,
  });
}

export function useFazenda(id: string | undefined) {
  return useQuery<FichaFazendaDTO>({
    queryKey: [ORGS, id],
    queryFn: () => fetchJson<FichaFazendaDTO>(`${ORGS}/${id}`),
    enabled: !!id,
  });
}

function invalidarFazendas(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: [ORGS] });
  qc.invalidateQueries({ queryKey: [ORGS, id] });
  qc.invalidateQueries({ queryKey: ["/api/plataforma/visao-geral"] });
}

export function useAtualizarFazenda(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campos: PatchFazenda) =>
      apiRequest("PATCH", `${ORGS}/${id}`, campos).then(r => r.json()) as Promise<FazendaDTO>,
    onSuccess: () => invalidarFazendas(qc, id),
  });
}

export function useCortesia(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: CortesiaInput) =>
      apiRequest("POST", `${ORGS}/${id}/cortesia`, dados).then(r => r.json()) as Promise<{ ok: true; org: FazendaDTO }>,
    onSuccess: () => invalidarFazendas(qc, id),
  });
}

// ─── P2: nova fazenda, ações de usuário, acessos de suporte ──────────────────

export interface NovaFazendaInput {
  nomeFazenda: string;
  tipo: "PRODUTOR" | "COMPRADOR";
  praca?: string | null;
  cidade?: string | null;
  responsavel: { nome: string; email?: string | null; telefone?: string | null };
  modulesEnabled?: string[];
  regime: "TRIAL_15" | "CORTESIA" | "CORTESIA_ATE";
  cortesiaAte?: string | null;
}

export interface NovaFazendaResultado {
  org: FazendaDTO;
  admin: { id: string; name: string; email: string | null; telefone: string | null };
  convite: LinkAcesso | null;
}

export function useCriarFazenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: NovaFazendaInput) =>
      apiRequest("POST", ORGS, dados).then(r => r.json()) as Promise<NovaFazendaResultado>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ORGS] });
      qc.invalidateQueries({ queryKey: ["/api/plataforma/visao-geral"] });
    },
  });
}

/** reset-link · desativar · reativar · plataforma-admin — pela ficha da fazenda. */
export function useAcaoUsuarioFazenda(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, acao, corpo }: { userId: string; acao: "reset-link" | "desativar" | "reativar" | "plataforma-admin"; corpo?: unknown }) =>
      apiRequest("POST", `${ORGS}/${orgId}/usuarios/${userId}/${acao}`, corpo).then(r => r.json()) as Promise<{ convite?: LinkAcesso; name?: string; telefone?: string | null }>,
    onSuccess: () => invalidarFazendas(qc, orgId),
  });
}

export interface AcessoSuporteDTO {
  id: string;
  orgId: string;
  solicitanteUserId: string;
  motivo: string;
  duracaoHoras: number;
  status: "PENDENTE" | "AUTORIZADO" | "RECUSADO" | "ENCERRADO" | "EXPIRADO";
  autorizadoPorUserId: string | null;
  autorizadoEm: string | null;
  expiraEm: string | null;
  encerradoEm: string | null;
  criadoEm: string;
}

export function useAcessosDaFazenda(orgId: string | undefined) {
  return useQuery<AcessoSuporteDTO[]>({
    queryKey: [ORGS, orgId, "acessos"],
    queryFn: () => fetchJson<AcessoSuporteDTO[]>(`${ORGS}/${orgId}/acessos`),
    enabled: !!orgId,
  });
}

export function useSolicitarAcesso(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { motivo: string; duracaoHoras: number }) =>
      apiRequest("POST", `${ORGS}/${orgId}/acessos`, dados).then(r => r.json()) as Promise<AcessoSuporteDTO>,
    onSuccess: () => qc.invalidateQueries({ queryKey: [ORGS, orgId, "acessos"] }),
  });
}

export function useEntrarComo() {
  return useMutation({
    mutationFn: (acessoId: string) =>
      apiRequest("POST", `/api/plataforma/acessos/${acessoId}/entrar`).then(r => r.json()) as Promise<{ ok: true; orgId: string; expiraEm: string | null }>,
  });
}

export function useSairSuporte() {
  return useMutation({
    mutationFn: () => apiRequest("POST", "/api/plataforma/suporte/sair").then(r => r.json()),
  });
}
